import { computed, effect, linkedSignal, resource, Signal, WritableSignal } from '@angular/core';
import { translator } from '@softline/ui-core';
import { requestErrorMessageFactory } from '@softline/application';
import { PageFilterValuePage } from '../services/data-source/page-filter-data.service';

export const PAGE_FILTER_VALUES_PAGE_SIZE = 50;
/** stop walking past windows that render nothing after this many consecutive pages */
export const PAGE_FILTER_VALUES_MAX_EMPTY_PAGES = 10;

export interface PagedFilterValuesOptions<TRequest extends { page: number }> {
  /** the request identity, memoized by the caller; `undefined` keeps the loader idle (closed panel) */
  request: Signal<TRequest | undefined>;
  /** the page the caller drives: a new search or a reopen sets it back to 0 */
  page: WritableSignal<number>;
  load: (request: TRequest, start: number, end: number) => Promise<PageFilterValuePage<any>>;
  pageSize?: number;
  maxEmptyPages?: number;
}

export interface PagedFilterValues<TValue> {
  /** the accumulated, renderable values: page 0 replaces, later pages append */
  readonly values: Signal<TValue[]>;
  /** a full window of ROWS came back - more pages may exist even if none of them rendered */
  readonly hasMore: Signal<boolean>;
  readonly loading: Signal<boolean>;
  /** a failed load; an in-flight or superseded request is not a failure */
  readonly failed: Signal<boolean>;
  /** the message the app already uses for this error class (network, 500, unauthorized, ...) */
  readonly errorDetail: Signal<string | null>;
  /** re-request the page that failed; loaded pages stay */
  retry(): void;
  /** continue past the guard that stopped the walk through windows that render nothing */
  resume(): void;
}

/**
 * The paged value list behind both pickers of the page filter (sidebar category and pinned
 * dropdown): one resource per request identity, a guard around `value()` so a failed load can
 * never throw out of a computed, a page-keyed accumulator that cannot be wiped without a
 * reload, and the walk through windows whose column values are all empty. Runs in an
 * injection context.
 */
export function pagedFilterValues<TValue, TRequest extends { page: number }>(
  options: PagedFilterValuesOptions<TRequest>
): PagedFilterValues<TValue> {
  const pageSize = options.pageSize ?? PAGE_FILTER_VALUES_PAGE_SIZE;
  const maxEmptyPages = options.maxEmptyPages ?? PAGE_FILTER_VALUES_MAX_EMPTY_PAGES;
  const t = translator();

  type LoadedPage = PageFilterValuePage<any> & { page?: number };

  const pageResource = resource<LoadedPage, TRequest | undefined>({
    params: () => options.request(),
    loader: async ({ params }) => {
      const result = await options.load(params, params.page * pageSize, (params.page + 1) * pageSize);
      return { ...result, page: params.page };
    },
    defaultValue: { values: [], rawCount: 0 },
  });

  // reading value() on an errored resource THROWS (ResourceValueError) - every read goes
  // through here so a failed load renders the error state instead of breaking change detection
  const loadedPage = computed<LoadedPage | null>(() =>
    pageResource.hasValue() ? pageResource.value() : null
  );

  // Keyed by the page the loader stamped on its result, not by options.page(): the page
  // signal moves before the resource does, so a read in between must not append the
  // current window twice. A failed or still-loading page 0 is a new search with nothing
  // to show yet; a failed later page keeps what is there.
  const EMPTY = { upTo: -1, values: [] as TValue[] };
  const accumulated = linkedSignal<
    { page: number; loaded: LoadedPage | null; failed: boolean },
    { upTo: number; values: TValue[] }
  >({
    source: computed(() => ({
      page: options.page(),
      loaded: loadedPage(),
      failed: pageResource.status() === 'error',
    })),
    computation: ({ page, loaded, failed }, previous) => {
      const kept = previous?.value ?? EMPTY;
      if (failed || loaded?.page === undefined) return page === 0 ? EMPTY : kept;
      const values = loaded.values as TValue[];
      if (loaded.page === 0) return { upTo: 0, values };
      if (loaded.page <= kept.upTo) return kept;
      return { upTo: loaded.page, values: [...kept.values, ...values] };
    },
  });
  const values = computed(() => accumulated().values);

  const hasMore = computed(() => (loadedPage()?.rawCount ?? 0) >= pageSize);
  const loading = computed(() => pageResource.isLoading());
  const failed = computed(() => pageResource.status() === 'error');

  const errorDetail = computed<string | null>(() => {
    const error = failed() ? pageResource.error() : null;
    if (!error) return null;
    const message = requestErrorMessageFactory(error);
    if (!message) return null;
    const text = t(message.message)();
    // the application i18n module may not be loaded here - never leak a raw key into the UI
    return text && !text.startsWith('#') ? text : null;
  });

  // A window can be full of rows whose column value is empty: nothing renders, so there is
  // nothing to scroll and the list would end early. Walk on until something renders, the
  // result is exhausted, or the guard trips (the caller's empty state then offers resume()).
  let emptyPages = 0;
  effect(() => {
    // page 0 means a new search or a reopened panel: the walk starts over
    if (options.page() === 0) emptyPages = 0;
    if (loading() || failed() || !hasMore()) return;
    if (values().length > 0) return;
    if (emptyPages >= maxEmptyPages) return;
    emptyPages++;
    options.page.update((page) => page + 1);
  });

  return {
    values,
    hasMore,
    loading,
    failed,
    errorDetail,
    retry: () => pageResource.reload(),
    resume: () => {
      emptyPages = 0;
      options.page.update((page) => page + 1);
    },
  };
}
